import type { CityConfig } from "../city-template/types";

export const lancasterConfig: CityConfig = {
  city: "Lancaster",
  state: "Ohio",
  stateAbbr: "OH",
  slug: "lancaster-oh",
  route: "/cities/lancaster-oh",
  title: "Emergency Passport Lancaster, OH | Expedited Passport Services | Fast Passport Center",
  metaDescription: "Emergency and expedited passport services in Lancaster, OH — as fast as 24 hours. Same-day courier processing available. A+ BBB rated, registered State Dept. courier serving Fairfield County and southeast Columbus suburbs. Get your passport fast!",
  h1: "Emergency & Expedited Passport Services in Lancaster, OH",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Lancaster and southeast Columbus area travelers — including Pickerington and Canal Winchester — get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Lancaster%20Ohio%20historic%20downtown%20with%20charming%20brick%20buildings%20and%20the%20iconic%20Georgian%20Hotel%2C%20beautiful%20Fairfield%20County%20courthouse%20square%2C%20warm%20golden%20hour%20lighting%20on%20a%20classic%20American%20small%20town%20main%20street%2C%20well-preserved%20historic%20architecture%20with%20trees%20and%20hanging%20flower%20baskets%2C%20professional%20photography%20with%20warm%20earth%20tones%20and%20a%20peaceful%20atmosphere&width=1800&height=700&seq=lancaster-oh-hero-001&orientation=landscape",
  heroImageAlt: "Lancaster Ohio historic downtown with Georgian Hotel and courthouse square",
  geoPosition: "39.7137;-82.5993",
  geoPlacename: "Lancaster, Ohio",
  regionalAgency: {
    name: "Chicago Passport Agency",
    address: "230 S Dearborn St, Suite 380, Chicago, IL 60604",
    distance: "Located in downtown Chicago — approximately 300 miles from Lancaster via I-70",
  },
  nearbyCitiesZips: [
    { name: "Lancaster", zips: "43130" },
    { name: "Pickerington", zips: "43147" },
    { name: "Canal Winchester", zips: "43110" },
    { name: "Amanda", zips: "43102" },
    { name: "Baltimore", zips: "43105" },
    { name: "Carroll", zips: "43112" },
    { name: "Reynoldsburg", zips: "43068" },
    { name: "Groveport", zips: "43125" },
    { name: "Circleville", zips: "43113" },
    { name: "Logan", zips: "43138" },
    { name: "Thornville", zips: "43076" },
    { name: "Hebron", zips: "43025" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Lancaster departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-global-line", title: "Fairfield County Coverage", desc: "Serving Lancaster, Pickerington, and southeast Columbus suburbs" },
  ],
  internalLinks: [
    { name: "Expedited passport services in Columbus, OH", route: "/cities/columbus-oh" },
    { name: "Fast passport renewal in Newark, OH", route: "/cities/newark-oh" },
    { name: "Emergency passport help in Delaware, OH", route: "/cities/delaware-oh" },
    { name: "Urgent passport services in Dayton, OH", route: "/cities/dayton" },
    { name: "Same-day passport help in Cincinnati, OH", route: "/cities/cincinnati" },
    { name: "Passport courier services in Reynoldsburg, OH", route: "/cities/reynoldsburg" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Lancaster remotely via secure courier. We do not have a walk-in office in Lancaster — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Fairfield County and southeast Columbus area travelers.",
  fpcOfficeDistance: "Serving all of Fairfield County and southeast Columbus suburbs",
  serviceAreaIntro: "From downtown Lancaster to Pickerington and Canal Winchester, from Amanda to Baltimore and Carroll — Fast Passport Center serves the entire Fairfield County and southeast Columbus region. We regularly help travelers from Reynoldsburg, Groveport, and Circleville get their passports fast.",
  nearbyCitiesIntro: "We serve all Lancaster neighborhoods and the surrounding Fairfield County and southeast Columbus region including Pickerington, Canal Winchester, Amanda, Baltimore, Carroll, Reynoldsburg, Groveport, Circleville, Logan, Thornville, and Hebron. If you are anywhere in the Lancaster area or southeast Columbus suburbs, we have you covered.",
  howItWorksIntro: "Most Lancaster travelers complete everything online and ship documents overnight. No need to drive to Chicago or wait in line at the regional agency.",
  mistakesIntro: "Lancaster travelers often face long drives to the nearest regional passport agency. Here are the most common mistakes that cause delays.",
  testimonialsIntro: "Lancaster and Fairfield County travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Lancaster? Here are the most common ones we hear from Fairfield County travelers.",
  statePageRoute: "/states/ohio",
  statePageName: "Ohio",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "3–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Lancaster international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "Christina L.",
      location: "Lancaster, OH",
      rating: 5,
      text: "My passport expired two weeks before our honeymoon in Jamaica. Fast Passport Center had my renewal ready in 4 days. They were incredibly helpful and communicative throughout.",
      service: "Passport Renewal",
    },
    {
      name: "Steven P.",
      location: "Pickerington, OH",
      rating: 5,
      text: "Business trip to Tokyo came up with only 4 days notice. Fast Passport Center got my emergency passport processed in 36 hours. Outstanding service.",
      service: "Emergency Passport",
    },
    {
      name: "Rebecca M.",
      location: "Canal Winchester, OH",
      rating: 5,
      text: "Applied for my daughter's first passport for our Disney cruise. They guided us through every document and had her passport ready in 5 days. Highly recommend.",
      service: "Child Passport",
    },
    {
      name: "Daniel K.",
      location: "Reynoldsburg, OH",
      rating: 5,
      text: "Lost my passport the week before my Costa Rica trip. Fast Passport Center handled everything and got me a replacement in 4 days. They saved my vacation.",
      service: "Lost Passport Replacement",
    },
  ],
  facilities: [
    {
      id: "lancaster-1",
      name: "Lancaster Main Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "150 E Main St",
      city: "Lancaster",
      state: "OH",
      zip: "43130",
      phone: "(740) 653-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Downtown Lancaster",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Main downtown facility. High volume — appointments strongly recommended. Photo services available on-site.",
      icon: "ri-mail-line",
    },
    {
      id: "lancaster-2",
      name: "Pickerington Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "1001 Hill Rd N",
      city: "Pickerington",
      state: "OH",
      zip: "43147",
      phone: "(614) 837-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Pickerington, 15 miles northeast",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Pickerington and Reynoldsburg residents. Less crowded than downtown. Call for appointment.",
      icon: "ri-mail-line",
    },
    {
      id: "lancaster-3",
      name: "Canal Winchester Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "10 N High St",
      city: "Canal Winchester",
      state: "OH",
      zip: "43110",
      phone: "(614) 837-2512",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Canal Winchester, 10 miles north",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Canal Winchester and Groveport residents. Ample parking. Call ahead for appointment availability.",
      icon: "ri-mail-line",
    },
    {
      id: "lancaster-4",
      name: "Circleville Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "120 W Union St",
      city: "Circleville",
      state: "OH",
      zip: "43113",
      phone: "(740) 477-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Pickaway County, 20 miles south",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Alternative for south Lancaster and Pickaway County residents. Appointments required.",
      icon: "ri-mail-line",
    },
    {
      id: "lancaster-5",
      name: "Fairfield County Clerk of Courts",
      type: "Court Clerk Passport Acceptance",
      address: "224 E Main St",
      city: "Lancaster",
      state: "OH",
      zip: "43130",
      phone: "(740) 652-6000",
      hours: "Mon–Fri: 8:00 AM – 4:30 PM",
      distance: "Fairfield County Courthouse",
      website: "https://www.co.fairfield.oh.us/",
      websiteLabel: "Fairfield County Courts",
      notes: "Accepts passport applications. Good alternative to post office with extended hours and appointment availability.",
      icon: "ri-government-line",
    },
    {
      id: "lancaster-6",
      name: "Reynoldsburg Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "8500 E Main St",
      city: "Reynoldsburg",
      state: "OH",
      zip: "43068",
      phone: "(614) 866-2511",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "Reynoldsburg, 20 miles northwest",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Serves Reynoldsburg and Franklin County residents northwest of Lancaster. Call for appointment.",
      icon: "ri-mail-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — 5 hour drive to Chicago", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Lancaster photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Fairfield County acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Lancaster applicants show up with incorrect payment and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Fairfield County families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Lancaster travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Lancaster." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Lancaster address — trackable every step of the way." },
  ],
  faqs: [
    { q: "Can I get an emergency passport in Lancaster before international travel?", a: "Yes. Fast Passport Center provides emergency passport services in Lancaster for travelers with urgent international flights. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, achieving 24–48 hour turnaround — without the 5 hour drive to Chicago." },
    { q: "How fast can I renew my passport in Lancaster?", a: "Fast Passport Center offers fast passport renewal in Lancaster in as little as 24–48 hours through our emergency courier service. For less urgent travel, our 3–5 day expedited option provides the best value. Both options include full document review and a dedicated agent." },
    { q: "Can I get a same-day passport appointment near Lancaster?", a: "Same-day passport appointments are only available at the Chicago Regional Passport Agency — approximately 300 miles from Lancaster via I-70 — for documented life-or-death emergencies. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option for Fairfield County residents." },
    { q: "Can I expedite a child passport in Lancaster?", a: "Yes. Fast Passport Center expedites child passports in Lancaster for minors under 16. Both parents must appear or submit notarized consent, along with the child's birth certificate and both parents' IDs. We walk Fairfield County families through the entire process." },
    { q: "What should I do if I lost my passport before a trip?", a: "If you lost your passport before a trip, contact Fast Passport Center immediately. We provide lost passport replacement services in Lancaster with DS-64 form guidance and identity verification support. Our emergency courier service can get your replacement passport in 24–48 hours." },
    { q: "What is the closest passport agency to Lancaster?", a: "The nearest regional passport agency to Lancaster is the Chicago Passport Agency, approximately 300 miles away via I-70. For urgent processing without the long drive, Fast Passport Center's registered courier service achieves 24–48 hour turnaround from Lancaster without any in-person agency visit required." },
    { q: "Do I need to drive to the Chicago Passport Agency?", a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change." },
    { q: "Can you help travelers from Pickerington and Canal Winchester?", a: "Absolutely. Fast Passport Center serves all of Fairfield County and the surrounding southeast Columbus region including Pickerington, Canal Winchester, Amanda, Baltimore, Carroll, Reynoldsburg, Groveport, Circleville, Logan, Thornville, and Hebron. Our courier service works seamlessly from any zip code in the area." },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Lancaster", address: "1515 E Main St, Lancaster, OH 43130", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Lancaster", address: "1200 E Main St, Lancaster, OH 43130", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart Supercenter — Lancaster", address: "2680 N Memorial Dr, Lancaster, OH 43130", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Lancaster", address: "1600 E Main St, Lancaster, OH 43130", hours: "Mon–Sun: 9AM–9PM" },
    { name: "CVS Pharmacy — Pickerington", address: "1021 Hill Rd N, Pickerington, OH 43147", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Reynoldsburg", address: "8000 E Main St, Reynoldsburg, OH 43068", hours: "Mon–Sun: 7AM–11PM" },
    { name: "Walmart — Circleville", address: "1470 S Court St, Circleville, OH 43113", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Canal Winchester", address: "10 W Waterloo St, Canal Winchester, OH 43110", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "Lancaster BMV — Fairfield County",
      address: "1515 E Main St, Lancaster, OH 43130",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Driver's license and REAL ID services. Appointment strongly recommended.",
      featured: true,
    },
    {
      name: "Pickerington BMV — Franklin County",
      address: "1001 Hill Rd N, Pickerington, OH 43147",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Franklin County driver's license and REAL ID services. Alternative for northeast Lancaster residents.",
      featured: false,
    },
    {
      name: "Circleville BMV — Pickaway County",
      address: "600 E Main St, Circleville, OH 43113",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Pickaway County BMV. Alternative for south Lancaster and Circleville area residents.",
      featured: false,
    },
    {
      name: "Reynoldsburg BMV — Franklin County",
      address: "8500 E Main St, Reynoldsburg, OH 43068",
      hours: "Mon–Fri: 8AM–5PM",
      note: "Franklin County BMV. Alternative for northwest Lancaster and Reynoldsburg area residents.",
      featured: true,
    },
  ],
  airports: [
    {
      code: "LCK",
      name: "Rickenbacker International Airport",
      distance: "20 miles north of Lancaster",
      address: "2241 John Circle Dr, Columbus, OH 43217",
      note: "Regional airport serving central Ohio with cargo operations and limited passenger services. Commercial flights connect through Columbus.",
      flightUrl: "https://www.rickenbacker.org/",
      flightLabel: "Rickenbacker Airport",
      featured: true,
    },
    {
      code: "CMH",
      name: "John Glenn Columbus International Airport",
      distance: "30 miles north of Lancaster",
      address: "4600 International Gateway, Columbus, OH 43219",
      note: "Nearest major commercial airport with direct flights to major hubs. Primary airport for Lancaster international travelers.",
      flightUrl: "https://www.flycmh.com/",
      flightLabel: "Columbus Airport",
      featured: false,
    },
    {
      code: "DAY",
      name: "Dayton International Airport",
      distance: "80 miles west of Lancaster",
      address: "3600 Terminal Dr, Dayton, OH 45377",
      note: "Alternative airport for west Lancaster. Additional flight options and sometimes easier parking.",
      flightUrl: "https://flydayton.com/",
      flightLabel: "Dayton Airport",
      featured: false,
    },
  ],
};